// @ts-ignore
export enum PaymentErrorCode {
  // general
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  NETWORK_ERROR = "NETWORK_ERROR",
  INITIALIZATION_FAILED = "INITIALIZATION_FAILED",
  CONFIGURATION_ERROR = "CONFIGURATION_ERROR",
  VALIDATION_ERROR = "VALIDATION_ERROR",

  PROVIDER_UNAVAILABLE = "PROVIDER_UNAVAILABLE",
  AUTHENTICATION_FAILED = "AUTHENTICATION_FAILED",
  SESSION_CREATION_FAILED = "SESSION_CREATION_FAILED",
  PAYMENT_PROCESSING_FAILED = "PAYMENT_PROCESSING_FAILED",
  PAYMENT_DECLINED = "PAYMENT_DECLINED",
  USER_CANCELLED = "USER_CANCELLED",
  INVALID_RESPONSE = "INVALID_RESPONSE",
  APPLE_PAY_NOT_AVAILABLE = "APPLE_PAY_NOT_AVAILABLE",
  SCRIPT_LOAD_FAILED = "SCRIPT_LOAD_FAILED",

  // tamara
  TAMARA_NO_REDIRECT_URL = "TAMARA_NO_REDIRECT_URL",

  // moyasar
  MOYASAR_TOKEN_CREATION_FAILED = "MOYASAR_TOKEN_CREATION_FAILED",
  MOYASAR_SESSION_CREATION_FAILED = "MOYASAR_SESSION_CREATION_FAILED",
  MOYASAR_INVALID_TOKEN_RESPONSE = "MOYASAR_INVALID_TOKEN_RESPONSE",

  // bank transfer
  BANK_TRANSFER_INVALID_FILE = "BANK_TRANSFER_INVALID_FILE",
  BANK_TRANSFER_FILE_TOO_LARGE = "BANK_TRANSFER_FILE_TOO_LARGE",
  BANK_TRANSFER_INVALID_FILE_TYPE = "BANK_TRANSFER_INVALID_FILE_TYPE",

  // tabby
  TABBY_NOT_ELIGIBLE = "TABBY_NOT_ELIGIBLE",
  TABBY_SESSION_CREATION_FAILED = "TABBY_SESSION_CREATION_FAILED",

  // myfatoorah
  MYFATOORAH_SCRIPT_LOAD_FAILED = "MYFATOORAH_SCRIPT_LOAD_FAILED",
  MYFATOORAH_INITIALIZATION_FAILED = "MYFATOORAH_INITIALIZATION_FAILED",
  MYFATOORAH_SESSION_CREATION_FAILED = "MYFATOORAH_SESSION_CREATION_FAILED",

  // provider not ready
  PROVIDER_NOT_READY = "PROVIDER_NOT_READY"
}

export interface StandardizedPaymentError {
  code: PaymentErrorCode;
  message: string;
  userMessageKey?: string;
  providerMessage?: string;
  originalError?: any;
  method?: string;
  field?: string;
}

export class PaymentError extends Error implements StandardizedPaymentError {
  code: PaymentErrorCode;
  userMessageKey?: string;
  providerMessage?: string;
  originalError?: any;
  method?: string;
  field?: string;

  constructor(details: StandardizedPaymentError) {
    super(details.message);
    this.name = "PaymentError";
    this.code = details.code;
    this.userMessageKey = details.userMessageKey;
    this.providerMessage = details.providerMessage;
    this.originalError = details.originalError;
    this.method = details.method;
    this.field = details.field;
    Object.setPrototypeOf(this, PaymentError.prototype);
  }

  /**
   * Determines if this error is retryable based on its code
   */
  isRetryable(): boolean {
    const retryableCodes = [
      PaymentErrorCode.NETWORK_ERROR,
      PaymentErrorCode.PROVIDER_UNAVAILABLE,
      PaymentErrorCode.SCRIPT_LOAD_FAILED,
      PaymentErrorCode.SESSION_CREATION_FAILED,
      PaymentErrorCode.PAYMENT_PROCESSING_FAILED
    ];
    return retryableCodes.includes(this.code);
  }

  /**
   * Gets a user-friendly message key for i18n
   */
  getUserMessageKey(): string {
    if (this.userMessageKey) {
      return this.userMessageKey;
    }

    switch (this.code) {
      case PaymentErrorCode.NETWORK_ERROR:
        return "payment.errors.network";
      case PaymentErrorCode.PAYMENT_DECLINED:
        return "payment.errors.declined";
      case PaymentErrorCode.USER_CANCELLED:
        return "payment.errors.cancelled";
      case PaymentErrorCode.APPLE_PAY_NOT_AVAILABLE:
        return "payment.errors.applePay.notAvailable";
      case PaymentErrorCode.VALIDATION_ERROR:
        return "payment.errors.validation";
      case PaymentErrorCode.PROVIDER_UNAVAILABLE:
        return "payment.errors.providerUnavailable";
      default:
        return "payment.errors.generic";
    }
  }

  /**
   * Creates a PaymentError from a generic error object
   */
  static fromError(
    error: any,
    options: {
      code?: PaymentErrorCode;
      method?: string;
      field?: string;
      userMessageKey?: string;
    } = {}
  ): PaymentError {
    const {
      code = PaymentErrorCode.UNKNOWN_ERROR,
      method,
      field,
      userMessageKey
    } = options;

    let message = "Unknown payment error";
    let providerMessage: string | undefined;

    if (error instanceof Error) {
      message = error.message;
      providerMessage = error.message;
    } else if (typeof error === "string") {
      message = error;
      providerMessage = error;
    } else if (error && typeof error === "object") {
      if (error.message) {
        message = error.message;
        providerMessage = error.message;
      } else if (error.error) {
        message = error.error;
        providerMessage = error.error;
      } else if (error.detail) {
        message = error.detail;
        providerMessage = error.detail;
      } else {
        message = JSON.stringify(error);
        providerMessage = JSON.stringify(error);
      }
    }

    return new PaymentError({
      code,
      message,
      userMessageKey,
      providerMessage,
      originalError: error,
      method,
      field
    });
  }
}
